import { createContext, useContext, useEffect } from 'react';
import { matchPath } from 'react-router';

import type { AiScope } from '@/api/assistant';

export interface RecordScope {
  type: AiScope;
  id: string;
  /** What to call the record when it is not simply its kind, such as a lead’s deal. */
  noun?: string;
}

export const AssistantScopeContext = createContext<(scope: RecordScope | null) => void>(
  () => undefined,
);

export function useAssistantScope(type: AiScope, id?: string) {
  const publish = useContext(AssistantScopeContext);
  useEffect(() => {
    publish(id ? { type, id } : null);
    return () => {
      publish(null);
    };
  }, [publish, type, id]);
}

/** The kinds of record that have a page of their own and that the assistant can speak about. */
export type RouteRecordKind = 'deal' | 'customer' | 'job' | 'lead' | 'proposal';

export interface RouteRecord {
  readonly kind: RouteRecordKind;
  readonly id: string;
}

const RECORD_ID = /^[0-9a-f-]{36}$/i;

/** What the assistant reads for a record page that is itself something it can read. */
const SCOPE_OF_RECORD: Readonly<Partial<Record<RouteRecordKind, AiScope>>> = {
  deal: 'OPPORTUNITY',
  customer: 'CUSTOMER',
  job: 'APPOINTMENT',
};

/**
 * The record a path is the page of.
 *
 * Every address a record has counts: the deal hub keeps its tabs beneath the deal, and the old
 * consultation, closing-call and lost-sale addresses are tabs of it now, so one pattern holds them
 * all. A path that merely starts like a record's, such as `/jobs/new`, is not one: a record's id is
 * an id.
 *
 * @param pathname the current location
 * @returns the kind of record and its id, or null when the page is not a record's
 */
export function recordForPath(pathname: string): RouteRecord | null {
  const candidates: readonly (readonly [RouteRecordKind, string | undefined])[] = [
    ['deal', matchPath('/pipeline/:id/*', pathname)?.params.id],
    ['customer', matchPath('/customers/:id', pathname)?.params.id],
    ['job', matchPath('/jobs/:id', pathname)?.params.id],
    ['lead', matchPath('/leads/:id', pathname)?.params.id],
    ['proposal', matchPath('/proposals/:id', pathname)?.params.id],
  ];
  for (const [kind, id] of candidates) {
    if (id !== undefined && RECORD_ID.test(id)) {
      return { kind, id };
    }
  }
  return null;
}

/**
 * What the assistant can read on a record page without asking the server anything.
 *
 * A lead and a proposal are not something the assistant reads in their own right; each belongs to
 * a deal, which is, and `useRecordScope` finds that deal.
 *
 * @param pathname the current location
 * @returns the scope, or null for any other page
 */
export function scopeForPath(pathname: string): RecordScope | null {
  const record = recordForPath(pathname);
  const type = record === null ? undefined : SCOPE_OF_RECORD[record.kind];
  return record === null || type === undefined ? null : { type, id: record.id };
}
